import { Injectable } from "@angular/core";
import { UIStore } from "./ui.store";
import { select } from "@ngneat/elf";
import { combineLatest, map } from "rxjs";
import { NumberGridConfiguration } from "./ui.store";
import { StatisticsMetric } from "source/app/components/statistics/statistics.types";
import { LocationStateQuery } from "./location-state.query";

@Injectable({ providedIn: "root" })
export class UiQuery {
	constructor(
		private uiStore: UIStore,
		private locationStateQuery: LocationStateQuery,
	) {}

	getSnapshot = () => {
		return this.uiStore.store.getValue();
	};

	widgetOptions$ = this.uiStore.store.pipe(select((state) => state.widgetOptions));
	locked$ = this.uiStore.store.pipe(select((state) => state.lockedSettings));
	seatsUnavailable$ = this.uiStore.store.pipe(select((state) => state.seatsUnavailable));
	cartsInUse$ = this.uiStore.store
		.pipe(select((state) => state.cartsInUse))
		.pipe(map((value) => (value <= 0 ? 1 : value)));

	maxCartsInUse$ = this.locationStateQuery.rideOpsSettings$.pipe(
		map((settings) => Math.max(1, settings.dispatchUnits || 1)),
	);

	maxSeatsUnavailable$ = combineLatest([this.locationStateQuery.rideOpsSettings$, this.cartsInUse$]).pipe(
		map(([settings, cartsInUse]) => Math.max(0, (settings.dispatchUnitCapacity || 0) * cartsInUse)),
	);

	dispatchUnitCapacity$ = this.locationStateQuery.rideOpsSettings$.pipe(
		map((settings) => Math.max(0, settings.dispatchUnitCapacity || 0)),
	);

	unavailableUnitIds$ = this.uiStore.store.pipe(select((state) => state.unavailableUnitIds ?? []));
	unavailableSeatIdsByUnitId$ = this.uiStore.store.pipe(select((state) => state.unavailableSeatIdsByUnitId ?? {}));

	unavailableSeatCountForUnit = (unitId: string | null | undefined): number => {
		if (unitId == null) {
			return 0;
		}
		return (this.uiStore.store.getValue().unavailableSeatIdsByUnitId ?? {})[unitId]?.length ?? 0;
	};

	maximumCapacityPerDispatch$ = combineLatest([
		this.locationStateQuery.rideOpsSettings$,
		this.cartsInUse$,
		this.seatsUnavailable$,
	]).pipe(
		map(([settings, dispatchUnitsAvailable, seatsUnavailable]) => {
			let capacityPerDispatch = settings.dispatchUnitCapacity;

			if (dispatchUnitsAvailable === 1) {
				capacityPerDispatch -= seatsUnavailable;
			}

			if (capacityPerDispatch < 0) {
				capacityPerDispatch = 0;
			}

			return capacityPerDispatch;
		}),
	);

	allowChangingDispatchesWhenClosingLocation$ = this.uiStore.store.pipe(
		select((state) => state.allowChangingDispatchesWhenClosingLocation),
	);
	allowChangingRidersWhenClosingLocation$ = this.uiStore.store.pipe(
		select((state) => state.allowChangingRidersWhenClosingLocation),
	);
	defaultCartsHasBeenSet$ = this.uiStore.store.pipe(select((state) => state.defaultCartsHasBeenSet));
	numberGridConfiguration$ = this.uiStore.store.pipe(
		select(
			(state) =>
				state.widgetOptions.riders.numberGridConfiguration ||
				<NumberGridConfiguration>{
					amount: 100,
					amountConfigured: false,
					direction: "ascending",
				},
		),
	);
	dashboardStatisticsMetric$ = this.uiStore.store
		.pipe(select((state) => state.dashboardStatisticsMetric))
		.pipe(map((value) => (value == undefined ? StatisticsMetric.None : value)));

	// Operator-facing local override for the asset-dispatch metric. Emits `undefined` when no
	// override is set, in which case the widget falls back to the location default.
	assetDispatchMetric$ = this.uiStore.store.pipe(select((state) => state.assetDispatchMetric ?? "occupiedSeats"));

	// Operator-facing local choice of asset-dispatch input widget. Defaults to "selector" (the
	// counter) when unset, so the asset widget behaves exactly as before until the operator opts
	// into the numpad.
	assetDispatchInputMode$ = this.uiStore.store.pipe(select((state) => state.assetDispatchInputMode ?? "selector"));

	rideOpsUiV2OptIn$ = this.uiStore.store.pipe(select((state) => state.rideOpsUiV2OptIn));
	rideOpsUiV2FlagAvailable$ = this.uiStore.store.pipe(select((state) => state.rideOpsUiV2FlagAvailable));
	assetHierarchyPrototypeFlagAvailable$ = this.uiStore.store.pipe(
		select((state) => state.assetHierarchyPrototypeFlagAvailable),
	);
	dispatchUndoOptIn$ = this.uiStore.store.pipe(select((state) => state.dispatchUndoOptIn === true));
	rememberRiderCount$ = this.uiStore.store.pipe(select((state) => state.rememberRiderCount === true));

	getRideOpsUiV2OptIn = (): boolean => {
		return this.uiStore.store.getValue().rideOpsUiV2OptIn === true;
	};

	getRideOpsUiV2FlagAvailable = (): boolean => {
		return this.uiStore.store.getValue().rideOpsUiV2FlagAvailable === true;
	};

	getAssetHierarchyPrototypeFlagAvailable = (): boolean => {
		return this.uiStore.store.getValue().assetHierarchyPrototypeFlagAvailable === true;
	};

	getDispatchUndoOptIn = (): boolean => {
		return this.uiStore.store.getValue().dispatchUndoOptIn === true;
	};

	getRememberRiderCount = (): boolean => {
		return this.uiStore.store.getValue().rememberRiderCount === true;
	};
}
